import { ArrowRight, Calendar, Database } from 'lucide-react';
import Link from 'next/link';
import type { BlogCategory } from '@/hooks/use-blog-categories';

/** Link to the blog events listing page, used by the utility card and mobile menu. */
export const KAJIAN_EVENT_LINK = {
  name: 'Event',
  href: '/blog/event',
};

/** Small uppercase column heading used above the category grid and the event card. */
const TITLE_CLASS =
  'px-3 pt-1 pb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500';

/** Heading above the category grid in the Kajian dropdown. */
export function KajianCategoryTitle() {
  return (
    <>
      <p className={TITLE_CLASS}>Kategori Kajian</p>
      <div className="border-t border-neutral-200 my-1" />
    </>
  );
}

/** Heading above the upcoming event card in the Kajian dropdown. */
export function KajianEventTitle() {
  return (
    <>
      <p className={TITLE_CLASS}>Event</p>
      <div className="border-t border-neutral-200 my-1" />
    </>
  );
}

/**
 * Two-column grid of blog categories linking to their filtered post lists.
 * Categories without published articles are rendered as non-interactive
 * placeholders so visitors cannot click into an empty listing.
 */
export function KajianCategoryGrid({ categories }: { categories: BlogCategory[] }) {
  return (
    <div className="columns-2 gap-x-4">
      {categories.map((category) => {
        // A definitive zero article count disables the entry; unknown counts
        // (fallback data before the API responds) stay clickable.
        if (category.articleCount === 0) {
          return (
            <div
              key={category.slug}
              aria-disabled="true"
              title="Kategori belum memiliki artikel"
              className="block break-inside-avoid px-3 py-1.5 rounded-lg cursor-default text-left"
            >
              <div className="text-xs font-medium text-neutral-300">{category.name}</div>
            </div>
          );
        }

        return (
          <Link
            key={category.slug}
            href={`/blog/posts?category=${category.slug}`}
            className="block break-inside-avoid px-3 py-1.5 rounded-lg hover:bg-neutral-200 transition-colors text-left"
          >
            <div className="text-xs font-medium text-neutral-900">{category.name}</div>
          </Link>
        );
      })}
    </div>
  );
}

interface UtilityCardProps {
  href: string;
  icon: 'database' | 'calendar';
  title: string;
  description: string;
}

/**
 * Utility card shown below the category grid, with an icon, a short title,
 * a one-line description and a trailing "Buka" affordance. Used for the
 * Regulation Hub and the events listing.
 */
function UtilityCard({ href, icon, title, description }: UtilityCardProps) {
  const Icon = icon === 'database' ? Database : Calendar;
  return (
    <Link
      href={href}
      className="group flex flex-1 flex-col justify-between rounded-lg border border-neutral-200 p-3 transition-colors hover:border-amber-300 hover:bg-amber-50/60"
    >
      <div>
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-500/10">
          <Icon className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
        </div>
        <p className="mt-2 text-xs font-semibold leading-snug text-neutral-900">{title}</p>
        <p className="mt-1 text-[11px] leading-relaxed text-neutral-500">{description}</p>
      </div>
      <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 transition-colors group-hover:text-amber-600">
        Buka
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

/**
 * Compact destination cards filling the space below the category grid: the
 * first active regulation hub (when one exists) and the events listing.
 */
export function KajianUtilityCards({ hubHref }: { hubHref: string | null }) {
  return (
    <div className="mt-2 flex flex-1 gap-2 px-1.5 pb-1.5">
      {hubHref && (
        <UtilityCard
          href={hubHref}
          icon="database"
          title="Regulation Hub"
          description="Eksplorasi data & statistik peraturan"
        />
      )}
      <UtilityCard
        href={KAJIAN_EVENT_LINK.href}
        icon="calendar"
        title="Lihat Event"
        description="Temukan event & webinar terdekat"
      />
    </div>
  );
}
